// FILE: src/components/app/UploadDataComponent/ProcessingDataComponent/ProcessingDataComponent.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { processUploadedData } from "../utils/processUploadedData";
import ProcessedDataComponent from "../ProcessedDataComponent/ProcessedDataComponent";

// Centralized helpers/constants
import { COLLECTIONS, userScopedId } from "../../../../../utils/firebase/core/collections";

// 🔁 Use shapers from writers (single source of truth)
import { shapeAccount } from "../../../../../utils/firebase/features/accounts/writers";
import { shapeTag } from "../../../../../utils/firebase/features/tags/writers";
import { shapeTransaction } from "../../../../../utils/firebase/features/transactions/writers";

import { AuthAPI, SystemAPI, TransactionsAPI } from "../../../../../utils/firebase";

/**
 * Props:
 *  - headers, rows: RAW table (CSV) shown on the upload page
 *  - mappingConfig: { mappings, defaults, mappedTargets } (from modal)
 *  - systemFormats, rawCsv
 */
export default function ProcessingDataComponent({ headers, rows, mappingConfig, systemFormats, rawCsv }) {
  const [user, setUser] = useState(null);

  const [processed, setProcessed] = useState(null);
  const [accountMapping, setAccountMapping] = useState({});
  const [accountTypes, setAccountTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  const [writing, setWriting] = useState(false);
  const [writeMsg, setWriteMsg] = useState("");

  // Init Firebase + auth listener
  useEffect(() => {
    AuthAPI.getFirebase();
    const unsub = AuthAPI.onAuthChangedSafe((u) => setUser(u));
    return () => unsub && unsub();
  }, []);

  // Load system account types
  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoadingTypes(true);
      try {
        const types = await SystemAPI.fetchAccountTypes();
        if (!ignore) setAccountTypes(types || []);
      } catch (e) {
        console.error("[ProcessingData] Failed to fetch account types:", e);
      } finally {
        if (!ignore) setLoadingTypes(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const canProcess = !!(headers?.length && rows?.length);

  const handleProcess = useCallback(() => {
    const out = processUploadedData({
      headers,
      rows,
      user,
      mappingConfig: mappingConfig || { defaults: {} },
    });
    setProcessed(out);
    setAccountMapping(Object.fromEntries((out.accounts || []).map((acc) => [acc.slug, null])));
  }, [headers, rows, user, mappingConfig]);

  const allAccountsMapped = useMemo(
    () => !!processed && processed.accounts.every((a) => !!accountMapping[a.slug]?.id),
    [processed, accountMapping]
  );

  const writeAll = useCallback(async () => {
    if (!user || !processed) return;
    setWriting(true);
    setWriteMsg("Starting…");
    try {
      await TransactionsAPI.writeProcessedData({
        processed,
        accountMapping,
        user,
        status: (msg) => setWriteMsg(msg),
      });
      setWriteMsg("Done.");
    } catch (e) {
      setWriteMsg(e?.message || "Failed.");
    } finally {
      setWriting(false);
    }
  }, [user, processed, accountMapping]);

  // -------- JSON previews via shapers --------
  const uid = user?.uid || "nouid";
  const email = (user?.email || "").toLowerCase();

  const jsonSummary = useMemo(() => {
    if (!processed) return null;
    const user_accounts = processed.accounts.map((a) => {
      const shaped = shapeAccount({ account: a, mappedType: accountMapping[a.slug] }) || {};
      return {
        ...shaped,
        accountBalance: shaped.accountBalance ?? a.accountBalance ?? 0,
        userId: uid,
        userEmail: email,
        createdAt: "<serverTimestamp>",
        updatedAt: "<serverTimestamp>",
      };
    });

    const user_tags = (processed.tags.nodes || [])
      .map((t) => {
        const shaped = shapeTag({ name: t.name, fullPath: t.fullPath, source: t.source }) || null;
        return shaped ? {
          ...shaped,
          userId: uid,
          userEmail: email,
          createdAt: "<serverTimestamp>",
          updatedAt: "<serverTimestamp>",
        } : null;
      })
      .filter(Boolean);

    const user_transactions = (processed.transactions || [])
      .slice(0, 200)
      .map((tx) => ({
        ...shapeTransaction(tx),
        id: "<auto-id>",
        userId: uid,
        userEmail: email,
        createdAt: "<serverTimestamp>",
        updatedAt: "<serverTimestamp>",
      }));

    return { user_accounts, user_tags, user_transactions };
  }, [processed, accountMapping, uid, email]);

  const jsonExamples = useMemo(() => {
    if (!processed) return null;

    const acc = processed.accounts[0] || null;
    const tag = (processed.tags.nodes || [])[0] || null;
    const tx  = (processed.transactions || [])[0] || null;

    const exAccount = acc ? {
      ...shapeAccount({ account: acc, mappedType: accountMapping[acc.slug] }),
      userId: uid, userEmail: email, createdAt: "<serverTimestamp>", updatedAt: "<serverTimestamp>",
    } : null;

    const exTag = tag ? {
      ...shapeTag({ name: tag.name, fullPath: tag.fullPath, source: tag.source }),
      userId: uid, userEmail: email, createdAt: "<serverTimestamp>", updatedAt: "<serverTimestamp>",
    } : null;

    const exTx = tx ? {
      ...shapeTransaction({ ...tx, accountId: userScopedId(uid, tx.accountId) }),
      id: "<auto-id>", userId: uid, userEmail: email, createdAt: "<serverTimestamp>", updatedAt: "<serverTimestamp>",
    } : null;

    return { user_accounts: exAccount, user_tags: exTag, user_transactions: exTx };
  }, [processed, accountMapping, uid, email]);

  return (
    <div className="mt-3">
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleProcess}
        disabled={!canProcess || !user}
        title={!user ? "Please sign in first" : "Analyze current table data"}
      >
        Process uploaded data
      </button>

      {processed && (
        <ProcessedDataComponent
          processed={processed}
          accountTypes={accountTypes}
          accountMapping={accountMapping}
          onMapAccount={(slug, typeObj) => setAccountMapping((m) => ({ ...m, [slug]: typeObj }))}
          loadingTypes={loadingTypes}
          jsonSummary={jsonSummary}
          jsonExamples={jsonExamples}
          canWrite={allAccountsMapped && !writing}
          onWrite={writeAll}
          writeState={{ writing, writeMsg }}
          onReplaceTransactions={(nextTxs) =>
            setProcessed((p) => ({
              ...p,
              transactions: Array.isArray(nextTxs) ? nextTxs : (p?.transactions || [])
            }))
          }
        />
      )}
    </div>
  );
}
